import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const VirtualFactoryTour = () => {
  const navigate = useNavigate();
  const [activeArea, setActiveArea] = useState(0);

  const factoryAreas = [
    {
      name: "Reception & Design Studio",
      description: "Where creativity meets technology. Our design studio is equipped with the latest software and color matching systems.",
      image: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg",
      features: ["Client Consultation Area", "Design Workstations", "Color Matching Lab", "Sample Display"],
      capacity: "20 concurrent projects",
      equipment: "Adobe Creative Suite, Pantone Color Systems"
    },
    {
      name: "Fabric Manufacturing Floor",
      description: "State-of-the-art fabric production facility operating with precision to deliver quality in every meter.",
      image: "https://images.pexels.com/photos/3184338/pexels-photo-3184338.jpeg",
      features: ["Modern Looms", "Quality Monitoring", "Real-time Quality Check", "Climate Control"],
      capacity: "10,000 meters/day",
      equipment: "Industrial Manufacturing Equipment"
    },
    {
      name: "Quality Control Lab",
      description: "Advanced testing facilities ensuring every product meets international quality standards.",
      image: "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg",
      features: ["Color Fastness Testing", "Fabric Analysis", "Print Quality Assessment", "Durability Testing"],
      capacity: "500 tests/day",
      equipment: "Spectrophotometers, Wash Fastness Testers"
    },
    {
      name: "Finishing & Packaging",
      description: "Final processing and packaging area where products are prepared for delivery with care and precision.",
      image: "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg",
      features: ["Steam Finishing", "Quality Inspection", "Custom Packaging", "Shipping Preparation"],
      capacity: "5,000 units/day",
      equipment: "Steam Presses, Automated Packaging Lines"
    },
    {
      name: "Warehouse & Logistics",
      description: "Climate-controlled storage and efficient logistics center ensuring timely delivery worldwide.",
      image: "https://images.pexels.com/photos/6069112/pexels-photo-6069112.jpeg",
      features: ["Climate Control", "Inventory Management", "Order Tracking", "Global Shipping"],
      capacity: "50,000 sq ft storage",
      equipment: "RFID Systems, Automated Sorting"
    }
  ];

  const factoryStats = [
    { icon: "Factory", value: "25,000", label: "Sq Ft Facility" },
    { icon: "Users", value: "50+", label: "Skilled Workers" },
    { icon: "Zap", value: "24/7", label: "Operations" },
    { icon: "Truck", value: "Global", label: "Shipping" }
  ];

  return (
    <section className="py-8 sm:py-12 lg:py-16 xl:py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-10 lg:mb-12 xl:mb-16">
          <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-3 sm:mb-4 lg:mb-6">
            <div className="w-8 sm:w-10 lg:w-12 h-1 bg-textile thread-weave"></div>
            <span className="text-textile font-poppins font-medium text-xs sm:text-sm lg:text-base">Factory Tour</span>
            <div className="w-8 sm:w-10 lg:w-12 h-1 bg-textile thread-weave"></div>
          </div>
          
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-poppins font-bold text-primary mb-3 sm:mb-4 lg:mb-6">
            Step Inside Our World
          </h2>
          
              <p className="text-xs sm:text-base lg:text-lg xl:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed px-4">
                Take a virtual journey through our state-of-the-art facility and witness 
                the precision and care that goes into every textile we create. We are experts in manufacturing position print fabrics with advanced printing technology.
              </p>
        </div>

        {/* Factory Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-8 sm:mb-10 lg:mb-12 xl:mb-16">
          {factoryStats?.map((stat, index) => (
            <div key={index} className="text-center p-3 sm:p-4 lg:p-6 bg-white rounded-lg sm:rounded-xl shadow-textile hover:shadow-heritage transition-shadow duration-300">
              <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-textile/10 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 lg:mb-4">
                <Icon name={stat?.icon} size={16} className="text-textile sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
              </div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-textile mb-1 lg:mb-2">{stat?.value}</div>
              <div className="text-[10px] sm:text-xs lg:text-sm text-text-secondary">{stat?.label}</div>
            </div>
          ))}
        </div>

        {/* Virtual Tour Interface */}
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 xl:gap-8 mb-8 sm:mb-10 lg:mb-12 xl:mb-16">
          {/* Area Selection */}
          <div className="space-y-3 sm:space-y-4">
            <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-primary mb-3 sm:mb-4">Explore Areas</h3>
            {factoryAreas?.map((area, index) => (
              <button
                key={index}
                onClick={() => setActiveArea(index)}
                className={`w-full text-left p-3 sm:p-4 rounded-lg transition-all duration-300 ${
                  activeArea === index
                    ? 'bg-textile text-white shadow-textile'
                    : 'bg-white hover:bg-muted border border-border'
                }`}
              >
                <h4 className={`text-sm sm:text-base font-semibold mb-1.5 sm:mb-2 ${
                  activeArea === index ? 'text-white' : 'text-primary'
                }`}>
                  {area?.name}
                </h4>
                <p className={`text-xs sm:text-sm ${
                  activeArea === index ? 'text-white/80' : 'text-text-secondary'
                }`}>
                  {area?.description?.substring(0, 80)}...
                </p>
              </button>
            ))}
          </div>

          {/* Active Area Display */}
          <div className="lg:col-span-2">
            <div className="card-elevated overflow-hidden">
              <div className="relative">
                <Image
                  src={factoryAreas?.[activeArea]?.image}
                  alt={factoryAreas?.[activeArea]?.name}
                  className="w-full h-56 sm:h-64 lg:h-72 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 text-white">
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-1.5 sm:mb-2">
                    {factoryAreas?.[activeArea]?.name}
                  </h3>
                  <div className="flex items-center space-x-3 sm:space-x-4 text-xs sm:text-sm">
                    <div className="flex items-center space-x-1">
                      <Icon name="Gauge" size={14} className="sm:w-4 sm:h-4" />
                      <span>{factoryAreas?.[activeArea]?.capacity}</span>
                    </div>
                  </div>
                </div>
                
                {/* Virtual Tour Button */}
                <button className="absolute top-3 sm:top-4 right-3 sm:right-4 bg-white/20 backdrop-blur-sm rounded-full p-2 sm:p-3 hover:bg-white/30 transition-all duration-300">
                  <Icon name="Camera" size={16} className="text-white sm:w-5 sm:h-5" />
                </button>
              </div>
              
              <div className="p-4 sm:p-5 lg:p-6">
                <p className="text-xs sm:text-sm lg:text-base text-text-secondary leading-relaxed mb-4 sm:mb-5 lg:mb-6">
                  {factoryAreas?.[activeArea]?.description}
                </p>
                
                <div className="grid sm:grid-cols-2 gap-4 sm:gap-5 lg:gap-6">
                  <div>
                    <h4 className="text-sm sm:text-base font-semibold text-primary mb-2 sm:mb-3">Key Features</h4>
                    <div className="space-y-1.5 sm:space-y-2">
                      {factoryAreas?.[activeArea]?.features?.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-textile rounded-full flex-shrink-0"></div>
                          <span className="text-xs sm:text-sm text-text-secondary">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm sm:text-base font-semibold text-primary mb-2 sm:mb-3">Equipment</h4>
                    <p className="text-xs sm:text-sm text-text-secondary leading-relaxed">
                      {factoryAreas?.[activeArea]?.equipment}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Interactive Map */}
        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-5 lg:p-6 xl:p-8 shadow-textile">
          <div className="grid lg:grid-cols-2 gap-5 sm:gap-6 lg:gap-8 items-center">
            <div>
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-poppins font-bold text-primary mb-3 sm:mb-4 lg:mb-6">
                Visit Us in Person
              </h3>
              <p className="text-xs sm:text-sm lg:text-base text-text-secondary leading-relaxed mb-3 sm:mb-4 lg:mb-6">
                Experience our facility firsthand with a guided tour. See our fabric manufacturing, 
                meet our team, and understand why we're Gujarat's premier fabric manufacturer and wholesaler.
              </p>
              
              <div className="space-y-2 sm:space-y-3 lg:space-y-4 mb-4 sm:mb-5 lg:mb-6">
                <div className="flex items-center space-x-2 lg:space-x-3">
                  <Icon name="MapPin" size={16} className="text-textile flex-shrink-0 sm:w-[18px] sm:h-[18px] lg:w-5 lg:h-5" />
                  <span className="text-[10px] sm:text-xs lg:text-sm text-text-secondary">
                    115, 116 Shree Darshan Industry Estate, Near Amrut Udhyog Nagar, Kamrej Road, Surat - 395010, Gujarat
                  </span>
                </div>
                <div className="flex items-center space-x-2 lg:space-x-3">
                  <Icon name="Clock" size={16} className="text-textile flex-shrink-0 sm:w-[18px] sm:h-[18px] lg:w-5 lg:h-5" />
                  <span className="text-[10px] sm:text-xs lg:text-sm text-text-secondary">
                    Monday - Saturday: 9:00 AM - 7:00 PM
                  </span>
                </div>
                <div className="flex items-center space-x-2 lg:space-x-3">
                  <Icon name="Phone" size={16} className="text-textile flex-shrink-0 sm:w-[18px] sm:h-[18px] lg:w-5 lg:h-5" />
                  <span className="text-[10px] sm:text-xs lg:text-sm text-text-secondary">
                    +91 99740 56700
                  </span>
                </div>
              </div>
              
              <button 
                onClick={() => navigate('/contact')}
                className="bg-textile text-white px-4 sm:px-5 lg:px-6 py-2 sm:py-2.5 lg:py-3 rounded-lg hover:bg-textile/90 transition-colors duration-300 flex items-center space-x-2 text-xs sm:text-sm lg:text-base font-semibold w-full sm:w-auto justify-center"
              >
                <Icon name="Calendar" size={16} className="sm:w-[18px] sm:h-[18px] lg:w-5 lg:h-5" />
                <span>Schedule Factory Visit</span>
              </button>
            </div>
            
            <div className="relative rounded-lg sm:rounded-xl overflow-hidden shadow-textile">
              <iframe
                width="100%"
                height="250"
                loading="lazy"
                title="Kunjvihari Impex Factory Location"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=21.2610001,72.9408181&z=14&output=embed"
                className="border-0 sm:h-[280px] lg:h-[300px]"
              ></iframe>
              <div className="absolute top-3 sm:top-4 left-3 sm:left-4 bg-white/90 backdrop-blur-sm rounded-lg p-2 sm:p-3">
                <div className="flex items-center space-x-1.5 sm:space-x-2">
                  <div className="w-2 h-2 lg:w-3 lg:h-3 bg-textile rounded-full animate-pulse"></div>
                  <span className="text-[10px] sm:text-xs lg:text-sm font-medium text-primary">Live Location</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VirtualFactoryTour;